var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"dc19de9d0793d8a0a632a997429dd811820500eb"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs"
import {
  DENY_URLS,
  IGNORE_ERRORS,
  matchesFilenameIgnorelist,
  tracesSampler,
} from "./SENTRY_CONSTANTS"

const SENTRY_DSN = process.env.SENTRY_DSN || process.env.NEXT_PUBLIC_SENTRY_DSN

const isProd =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "production" ||
  process.env.SENTRY_DEBUGGING === "true"

if (isProd) {
  Sentry.init({
    // Set release explicitly due to https://github.com/getsentry/sentry-javascript/issues/5734.
    // This should be injected in the Sentry app in Vercel, but it's not.
    release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,

    ignoreErrors: IGNORE_ERRORS,

    beforeSend(event, hint) {
      try {
        const errorMsg = hint.originalException?.toString()

        if (
          errorMsg &&
          IGNORE_ERRORS.some((ignore) => {
            if (typeof ignore === "string") {
              return errorMsg.includes(ignore)
            } else if (ignore instanceof RegExp) {
              return ignore.test(errorMsg)
            }
            return false
          })
        ) {
          return null
        }

        if (!event.exception) return event
        if (!event.exception.values) return event
        if (!event.exception.values[0]) return event
        if (!event.exception.values[0].stacktrace) return event
        if (!event.exception.values[0].stacktrace.frames) return event

        const frames = event.exception.values[0].stacktrace.frames

        for (const frame of frames) {
          const matches =
            matchesFilenameIgnorelist(frame.filename) ||
            matchesFilenameIgnorelist(frame.module) ||
            matchesFilenameIgnorelist(frame.function)
          if (matches) return null
        }
      } catch (e) {
        Sentry.addBreadcrumb({
          message: "Error filtering Sentry event",
          data: {
            error: e,
            event: event,
          },
        })
        Sentry.captureMessage("Error filtering client Sentry event")
        console.error("Error filtering Sentry event", e)
      }

      return event
    },

    denyUrls: DENY_URLS,

    dsn:
      SENTRY_DSN ||
      "https://bb6c39c632ef45069bd0c57bdcd2bc6c@o1337141.ingest.sentry.io/6606494",
    tracesSampler: (_samplingContext) => {
      return tracesSampler(_samplingContext, 0.35)
    },

    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,

    enabled: isProd,
    debug: process.env.SENTRY_DEBUGGING === "true",

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0.2,

    tracePropagationTargets: ["paragraph.xyz/api"],

    integrations: [
      Sentry.browserTracingIntegration({
        //traceFetch: false,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
      Sentry.browserProfilingIntegration(),
    ],

    // ...
    // Note: if you want to override the automatic release value, do not set a
    // `release` value here - use the environment variable `SENTRY_RELEASE`, so
    // that it will also get attached to your source maps
  })
}
